import React, { useEffect, useId, useState } from 'react';
import ErrorCodes from '../../services/ErrorCodes';
import './ChangePassPopup.css';

const MIN_LENGTH = 8;
const MAX_LENGTH = 128;

// Client-side mirror of the backend rule: 8–128 characters, no spaces.
const validate = (password, confirm) => {
    if (password.length < MIN_LENGTH) return `Минимум ${MIN_LENGTH} символов`;
    if (password.length > MAX_LENGTH) return `Максимум ${MAX_LENGTH} символов`;
    if (/\s/.test(password)) return 'Пароль не должен содержать пробелы';
    if (password !== confirm) return 'Пароли не совпадают';
    return null;
};

// `handleChangePass(password)` resolves to true on success, false (or throws) otherwise.
const ChangePassPopup = ({ handleChangePass, closePopup, error }) => {
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [touched, setTouched] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const titleId = useId();

    const validationError = validate(password, confirm);
    const serverError = !error
        ? null
        : error?.data?.code === -1
            ? 'Не удалось связаться с сервером, попробуйте ещё раз'
            : ErrorCodes.getText(error);
    const shownError = touched ? validationError : null;
    const lengthOk = password.length >= MIN_LENGTH && password.length <= MAX_LENGTH;
    const spacesOk = password.length > 0 && !/\s/.test(password);
    const matchOk = confirm.length > 0 && password === confirm;

    useEffect(() => {
        const onKey = (event) => {
            if (event.key === 'Escape') closePopup?.();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [closePopup]);

    const submit = async (event) => {
        event.preventDefault();
        setTouched(true);
        if (validationError || submitting) return;
        setSubmitting(true);
        try {
            const ok = await handleChangePass(password);
            if (ok !== false) setDone(true);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="cp-popup-container"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) closePopup?.();
            }}
        >
            <section className="cp-popup" role="dialog" aria-modal="true" aria-labelledby={titleId}>
                <header className="cp-popup__header">
                    <div>
                        <h2 id={titleId}>Сменить пароль</h2>
                        <p>Новый пароль сразу заменит текущий</p>
                    </div>
                    <button className="ui-close" type="button" onClick={closePopup} aria-label="Закрыть">×</button>
                </header>

                {done ? (
                    <div className="cp-popup__done">
                        <span className="cp-popup__done-icon" aria-hidden="true">✓</span>
                        <h3>Пароль изменён</h3>
                        <p>Используйте новый пароль при следующем входе.</p>
                        <button type="button" className="ui-btn ui-btn--block" onClick={closePopup}>Готово</button>
                    </div>
                ) : (
                    <form className="cp-popup__form" onSubmit={submit} noValidate>
                        <label className="cp-field">
                            <span className="cp-field__label">Новый пароль</span>
                            <span className="cp-field__control">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    autoComplete="new-password"
                                    autoFocus
                                    onChange={(event) => setPassword(event.target.value)}
                                    placeholder="Не меньше 8 символов"
                                />
                                <button
                                    type="button"
                                    className="cp-field__toggle"
                                    onClick={() => setShowPassword((value) => !value)}
                                    aria-pressed={showPassword}
                                >
                                    {showPassword ? 'Скрыть' : 'Показать'}
                                </button>
                            </span>
                        </label>

                        <label className="cp-field">
                            <span className="cp-field__label">Повторите пароль</span>
                            <span className="cp-field__control">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={confirm}
                                    autoComplete="new-password"
                                    onChange={(event) => setConfirm(event.target.value)}
                                    onBlur={() => setTouched(true)}
                                    placeholder="Ещё раз"
                                />
                            </span>
                        </label>

                        <ul className="cp-rules" aria-label="Требования к паролю">
                            <li className={lengthOk ? 'is-ok' : ''}>От 8 до 128 символов</li>
                            <li className={spacesOk ? 'is-ok' : ''}>Без пробелов</li>
                            <li className={matchOk ? 'is-ok' : ''}>Пароли совпадают</li>
                        </ul>

                        {shownError || serverError ? (
                            <p className="cp-popup__error" role="alert">{shownError || serverError}</p>
                        ) : null}

                        <div className="cp-popup__actions">
                            <button type="button" className="ui-btn ui-btn--secondary" onClick={closePopup}>Отмена</button>
                            <button type="submit" className="ui-btn" disabled={submitting || (touched && !!validationError)}>
                                {submitting ? 'Сохраняем…' : 'Сменить пароль'}
                            </button>
                        </div>
                    </form>
                )}
            </section>
        </div>
    );
};

export default ChangePassPopup;
